"use client"
import { useEffect, useRef } from "react"
import { useMetricDetails } from "./metricDetailsContext"
import { MetricDetailsContent } from "./MetricDetailsContent"
import {
  MetricPanelRedesign,
  type MetricPanelDesignId,
} from "./panel-redesigns/metricPanelRedesigns"
import { METRIC_LABELS } from "./metricLabels"
import { GalaxyHide } from "../blocks/layout/GalaxyHide"
import { useHudContext, useHudDates } from "../shared/playbookHUDInfra"
import type { MetricId } from "./metricDetailsTypes"

// The details layer's body: an open metric rendered as an alternative HUD
// state — HUD styling throughout (page background, mono uppercase labels,
// hairline rules, no enclosing box). It lives in a permanent overlay ABOVE
// the variant layer (see HudShellContainer), which keeps the globe/graph
// mounted underneath and simply fades them — no remount seams.
//
// `metricId` is the displayed metric — it outlives openId during the close
// fade, so the content doesn't vanish mid-dissolve. `panelDesign` picks one of
// the panel-redesign treatments (per hidden HUD tab — see
// HUD_METRIC_PANEL_DESIGNS in variants.tsx); null renders the production
// content.
export const MetricDetailsHudState = ({
  metricId,
  panelDesign = null,
}: {
  metricId: MetricId
  panelDesign?: MetricPanelDesignId | null
}) => {
  const ctx = useHudContext()
  const { targetDate } = useHudDates()
  const { openId, originEl, close } = useMetricDetails()
  const scrollRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!openId) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [openId, close])

  // Keyboard continuity across the crossfade: opening hides the layer that
  // held the focused trigger (focus would silently fall to <body>), so move
  // it into the details; closing hands it back to the trigger.
  useEffect(() => {
    if (openId) scrollRef.current?.focus({ preventScroll: true })
    else if (originEl?.isConnected) originEl.focus({ preventScroll: true })
  }, [openId, originEl])

  // Freeze the year while closed: this layer stays mounted behind the
  // crossfade after the first open, and a live year would keep the hidden
  // chart tweening (and the content re-rendering) on every scroll keyframe.
  // Same pattern as HudExtrapolatedGraphBlock's paused dates.
  const liveYear = targetDate.getFullYear() + targetDate.getMonth() / 12
  const lastOpenYearRef = useRef(liveYear)
  if (openId) lastOpenYearRef.current = liveYear
  const year = openId ? liveYear : lastOpenYearRef.current

  return (
    <GalaxyHide>
      <div
        ref={scrollRef}
        // The triggers advertise aria-haspopup="dialog"; this surface is that
        // dialog (non-modal — the article beside it stays live). tabIndex -1
        // makes it the focus target on open.
        role="dialog"
        aria-modal="false"
        aria-label={`${METRIC_LABELS[metricId]} — sources and details`}
        tabIndex={-1}
        // Scrolls the full column height; content fades out at the bottom
        // edge instead of hard-clipping. overscroll-contain keeps wheel
        // momentum at the edges from scrolling the story (and the HUD year)
        // underneath.
        className="scrollbar-hide absolute inset-0 overflow-y-auto overscroll-contain outline-none [mask-image:linear-gradient(to_bottom,black_calc(100%-28px),transparent)]"
      >
        {panelDesign ? (
          <MetricPanelRedesign designId={panelDesign} metricId={metricId} year={year} onClose={close} />
        ) : (
          <MetricDetailsContent metricId={metricId} year={year} onClose={close} withArticleDivider />
        )}
        {/* Breathing room so the final line clears the bottom fade mask. */}
        <div className="h-7" aria-hidden />
      </div>
    </GalaxyHide>
  )
}
